import React, { useMemo } from 'react';
import {
    Box,
    Chip,
    Divider,
    IconButton,
    Link,
    Modal,
    Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';

const styleModal = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '92%', md: '70%', lg: '54%' },
    maxHeight: '85vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    borderRadius: '0.5rem',
    boxShadow: 24,
    py: '1.5rem',
    px: { xs: '1rem', md: '1.5rem' },
    '&:focus-visible': { outline: 0 },
};

const labelStyle = {
    color: '#7C7B7B',
    fontSize: { xs: '0.75rem', lg: '0.875rem' },
    fontWeight: 500,
};

const valueStyle = {
    color: '#303030',
    fontSize: { xs: '0.875rem', lg: '1rem' },
    fontWeight: 500,
};

const shipmentStatusMap = {
    created: {
        label: 'Creado',
        colors: { bg: '#F4F4F4', text: '#303030', border: '#D8D8D8' },
    },
    exception: {
        label: 'Excepcion',
        colors: { bg: '#FFF0F0', text: '#B42318', border: '#F2B8B5' },
    },
    picked_up: {
        label: 'Recolectado',
        colors: { bg: '#E8F4FD', text: '#0B5394', border: '#B8DAF3' },
    },
    last_mile: {
        label: 'Ultima milla',
        colors: { bg: '#E8F4FD', text: '#0B5394', border: '#B8DAF3' },
    },
    delivery_attempt: {
        label: 'Intento de entrega',
        colors: { bg: '#FFF8E1', text: '#8A6D1A', border: '#F2E0A5' },
    },
    delivered: {
        label: 'Entregado',
        colors: { bg: '#EAF7EC', text: '#1F7A3D', border: '#B9E1C1' },
    },
    delivered_to_branch: {
        label: 'Entregado en sucursal',
        colors: { bg: '#EAF7EC', text: '#1F7A3D', border: '#B9E1C1' },
    },
    in_return: {
        label: 'En devolucion',
        colors: { bg: '#FFF0F0', text: '#B42318', border: '#F2B8B5' },
    },
    canceled: {
        label: 'Cancelado',
        colors: { bg: '#F4F4F4', text: '#5F5F5F', border: '#D8D8D8' },
    },
    error: {
        label: 'Error',
        colors: { bg: '#FFF0F0', text: '#B42318', border: '#F2B8B5' },
    },
    in_transit: {
        label: 'En transito',
        colors: { bg: '#E8F4FD', text: '#0B5394', border: '#B8DAF3' },
    },
};

const getShipmentStatusMeta = (status) => {
    if (!status) {
        return {
            key: 'unknown',
            label: 'N/A',
            colors: { bg: '#F4F4F4', text: '#5F5F5F', border: '#D8D8D8' },
        };
    }

    const normalizedStatus = String(status).toLowerCase().trim();
    const mappedStatus = shipmentStatusMap[normalizedStatus];

    if (mappedStatus) {
        return {
            key: normalizedStatus,
            label: mappedStatus.label,
            colors: mappedStatus.colors,
        };
    }

    return {
        key: normalizedStatus,
        label: normalizedStatus.replace(/_/g, ' '),
        colors: { bg: '#F4F4F4', text: '#5F5F5F', border: '#D8D8D8' },
    };
};

const buildShipmentBySku = (shipment = []) => {
    const shipmentBySku = new Map();

    shipment.forEach((box) => {
        const trackingData = box?.tracking_data || {};

        (box?.products || []).forEach((boxProduct) => {
            const sku = boxProduct?.sku;
            if (!sku) {
                return;
            }

            const currentTrackingList = shipmentBySku.get(sku) || [];
            const statusMeta = getShipmentStatusMeta(trackingData.status);
            currentTrackingList.push({
                carrier: trackingData.carrier || 'N/A',
                tracking_url: trackingData.tracking_url || null,
                status: statusMeta,
            });
            shipmentBySku.set(sku, currentTrackingList);
        });
    });

    return shipmentBySku;
};

const OrderShipmentDetailModal = ({ order, openModal, onCloseModal }) => {
    const shipmentDetails = useMemo(() => {
        const shipmentBySku = buildShipmentBySku(order?.shipment);

        return (order?.items || []).map((item) => {
            const tracking = shipmentBySku.get(item?.sku) || [];

            return {
                key: `${item?.sku || 'item'}-${item?.product?._id || ''}`,
                productName: item?.product?.productName || 'Producto sin nombre',
                sku: item?.sku || 'N/A',
                tracking: tracking.length
                    ? tracking
                    : [
                        {
                            carrier: 'N/A',
                            tracking_url: null,
                            status: getShipmentStatusMeta(null),
                        },
                    ],
            };
        });
    }, [order]);

    return (
        <Modal
            disableEscapeKeyDown
            slotProps={{ backdrop: { onClick: null } }}
            open={openModal}
            onClose={onCloseModal}
            aria-labelledby="order-shipment-detail-modal-title"
            sx={{
                backdropFilter: 'blur(2px)',
                '& .MuiModal-backdrop': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                },
            }}
        >
            <Box sx={styleModal}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '1rem' }}>
                    <Typography id="order-shipment-detail-modal-title" sx={{ color: '#303030', fontSize: '1.125rem', fontWeight: 700 }}>
                        Detalles de envio
                    </Typography>
                    <IconButton sx={{ padding: 0 }} onClick={onCloseModal}>
                        <CancelIcon sx={{ color: '#FC581F', fontSize: '1.6rem' }} />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {shipmentDetails.map((item, itemIndex) => (
                        <Box key={`${item.key}-${itemIndex}`} sx={{ border: '1px solid #E9E9E9', borderRadius: '0.5rem', p: '1rem' }}>
                            <Typography sx={{ color: '#303030', fontSize: { xs: '0.875rem', lg: '1rem' }, fontWeight: 700, mb: '0.25rem' }}>
                                {item.productName}
                            </Typography>
                            <Typography sx={{ ...labelStyle, mb: '0.75rem' }}>{`SKU: ${item.sku}`}</Typography>

                            {item.tracking.map((tracking, trackingIndex) => (
                                <Box key={`${item.key}-tracking-${trackingIndex}`} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {item.tracking.length > 1 && (
                                        <Typography sx={{ ...labelStyle, fontWeight: 700 }}>{`Caja ${trackingIndex + 1}`}</Typography>
                                    )}

                                    <Box>
                                        <Typography sx={labelStyle}>Carrier</Typography>
                                        <Typography sx={valueStyle}>{tracking.carrier || 'N/A'}</Typography>
                                    </Box>

                                    <Box>
                                        <Typography sx={labelStyle}>Tracking URL</Typography>
                                        {tracking.tracking_url ? (
                                            <Link
                                                href={tracking.tracking_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ ...valueStyle, color: '#2F69FF', textDecorationLine: 'underline' }}
                                            >
                                                {tracking.tracking_url}
                                            </Link>
                                        ) : (
                                            <Typography sx={valueStyle}>N/A</Typography>
                                        )}
                                    </Box>

                                    <Box>
                                        <Typography sx={labelStyle}>Status</Typography>
                                        <Chip
                                            label={tracking.status?.label || 'N/A'}
                                            size="small"
                                            sx={{
                                                mt: '0.125rem',
                                                backgroundColor: tracking.status?.colors?.bg || '#F4F4F4',
                                                color: tracking.status?.colors?.text || '#5F5F5F',
                                                border: `1px solid ${tracking.status?.colors?.border || '#D8D8D8'}`,
                                                '& .MuiChip-label': {
                                                    px: '0.5rem',
                                                    fontSize: { xs: '0.75rem', lg: '0.8125rem' },
                                                    fontWeight: 500,
                                                },
                                            }}
                                        />
                                    </Box>

                                    {trackingIndex + 1 < item.tracking.length && <Divider sx={{ mt: '0.25rem', borderColor: '#F4F4F4' }} />}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Modal>
    );
};

export default OrderShipmentDetailModal;
